// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@rpsc/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@rpsc/App/Components/Product/ProductGridTemplate";
import xUseWidgetBadges from "@rpsc/App/Hooks/xUseWidgetBadges";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import ProductPrice, { ProductPriceStyles } from "@rpsc/Overrides/Components/ProductPrice";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showTextBadges?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    maximumNumberOfTextBadges?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    showOriginPrice?: boolean;
    preserveSpaceForAvailability?: boolean;
    isGridView?: boolean;
    showInventoryPlaceHolder?: Boolean;
}
const mapStateToProps = (state: CustomAppState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isAuthenticated: xIsAuthenticated(state),
    isMobileScreen: state.xCommon.isMobileScreen,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    textBadgesStyles?: ProductTextBadgesStyles;
    bottomSectionContainer?: InjectableCss;
    emptySpace?: InjectableCss;
    emptySpaceForAvailability?: InjectableCss;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showTextBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    maximumNumberOfTextBadges = 4,
    productContext,
    canSeePrices,
    onAddToCartClick,
    showDisabled,
    isAuthenticated,
    containerClassName,
    isPdp,
    onChangeCompare,
    showOriginPrice = true,
    maximumNumberOfImageBadges = 4,
    showImageBadges,
    isMobileScreen,
    preserveSpaceForAvailability = false,
    isGridView = false,
    showInventoryPlaceHolder = false,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));

    const { imageBadges, textBadges } = xUseWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const callForPrice =
        productContext.productInfo.pricing && getUnitNetPrice(productContext.productInfo.pricing, 1).price === 0;
    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            <ProductGridContainer.PriceAndActions>
                <StyledWrapper {...styles.bottomSectionContainer}>
                    <ProductGridContainer.Badges>
                        {showTextBadges && !!textBadges?.length && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges}
                                extendedStyles={styles.textBadgesStyles}
                            />
                        )}
                        {showImageBadges && !!imageBadges?.length && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges} />
                        )}
                    </ProductGridContainer.Badges>

                    {showPrice && (
                        <ProductGridContainer.Price
                            {...{ isQuote: productContext.product.quoteRequired && isGridView }}
                        >
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showPack={false}
                                extendedStyles={styles.productPriceStyles}
                            />
                        </ProductGridContainer.Price>
                    )}
                    {canSeePrices && showAvailability && (
                        <ProductGridContainer.Availability>
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityStyles}
                                isProductDetailsPage={isPdp}
                                {...{ showPlaceholder: isGridView || showInventoryPlaceHolder }}
                            />
                            {preserveSpaceForAvailability &&
                                (productContext.product.isVariantParent ||
                                    (callForPrice && !productContext.product.quoteRequired)) && (
                                    <StyledWrapper
                                        {...styles.emptySpaceForAvailability}
                                        {...{ isVariantParent: productContext.product.isVariantParent }}
                                    />
                                )}
                        </ProductGridContainer.Availability>
                    )}

                    {canSeePrices && showAddToCartActions && (
                        <ProductGridContainer.AddToCartActions>
                            <ProductActions
                                {...productActionsProps}
                                onAddToCartClick={onAddToCartClick}
                                extendedStyles={styles.productActionsStyles}
                            />
                        </ProductGridContainer.AddToCartActions>
                    )}
                    {canSeePrices && (showAddToList || showCompare) && (
                        <ProductGridContainer.AddToList>
                            {showCompare && (
                                <ProductListProductImage
                                    showImage={false}
                                    showCompare={showCompare}
                                    extendedStyles={styles.productListProductImageStyles}
                                />
                            )}
                            {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                        </ProductGridContainer.AddToList>
                    )}
                </StyledWrapper>
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
